import { AccountSectionWrapper } from './AccountSectionWrapper'
import { AccountBadge } from './AccountBadge'
import { useIsExpired } from '../hooks/useIsExpired'
import { usePaginate } from '../hooks/usePaginate'
import { PaginateHeader } from './PaginateHeader'
import { formatMoney } from '@shopify/theme-currency'
import { accountTranslations } from '../helpers/translations'
// @ts-check
/**
 *  @typedef {import("../types/reward").RewardTabs} RewardTabs
 */

/**
 * @type {RewardTabs[]}
 */
const TABS = ['ACTIVE', 'PAST']

/**
 *
 * @param {{isActive: boolean}} param0
 */
export const RewardBadge = ({ isActive, remainingDays }) => {
  if (isActive) {
    if (remainingDays <= 21) {
      return <AccountBadge color="YELLOW" title="Expiring soon" />
    }
    return <AccountBadge color="BLUE" title="Active" />
  }

  return <AccountBadge color="YELLOW" title="Expired" />
}

/**
 *
 * @param {number} start
 * @param {number} expiry
 */
const getRemainingDays = (start, expiry) => {
  const differenceInMilliseconds = expiry - start
  const millisecondsInADay = 24 * 60 * 60 * 1000
  const daysApart = differenceInMilliseconds / millisecondsInADay
  return Math.round(daysApart)
}

/**
 *
 * @param {number} timestamp
 */
export const handleExpiryDate = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date().getTime()
  const remainingDays = getRemainingDays(now, date.getTime())

  return {
    expiryDate:
      date.toLocaleDateString('en-AU', { day: '2-digit' }) +
      '/' +
      date.toLocaleDateString('en-AU', { month: '2-digit' }) +
      '/' +
      date.toLocaleDateString('en-AU', { year: 'numeric' }),
    remainingDays,
    isExpired: now > timestamp
  }
}

export const AccountRewardsHistoryTabs = ({ tab, handleTabUpdate }) => {
  return (
    <div className={'flex flex-row gap-4'}>
      {TABS.map((t) => {
        const isSelected = tab === t
        return (
          <div
            onClick={() => {
              handleTabUpdate(t)
            }}
            className={`btn btn-md btn-inline btn-text ${isSelected ? 'underline underline-offset-1' : ''}`}
          >
            {t}
          </div>
        )
      })}
    </div>
  )
}

const renderLimit = 4

/**
 *
 * @param {{props: {rewards: any[]}}} props
 * @returns
 */
export const AccountRewardsHistory = ({ props }) => {
  const { timestamp, tab, handleTab } = useIsExpired()
  const rewardsData = props.rewards
    .filter((reward) => (tab !== 'ACTIVE' ? !reward.is_active : reward.is_active))
    .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at))
  const { config, page, handleNextPage, index } = usePaginate({ data: rewardsData, limit: renderLimit })

  return (
    <AccountSectionWrapper title={accountTranslations.my_rewards.history.title} anchorTag="rewards-history">
      <div className={'flex flex-row justify-between'}>
        <AccountRewardsHistoryTabs tab={tab} handleTabUpdate={handleTab} />
        <PaginateHeader
          props={{
            index,
            handleNextPage,
            renderLimit: 4,
            pageMin: config.pageMin,
            pageMax: config.pageMax
          }}
        />
      </div>

      <div className={'flex flex-col gap-2'}>
        <div className={'flex flex-row gap-4'}>
          <div className={'paragraph-sm w-full flex-grow font-bold'}>{accountTranslations.my_rewards.history.reward_type}</div>
          <div className={'paragraph-sm min-w-20 font-bold'}>{accountTranslations.my_rewards.history.balance}</div>
          <div className={'paragraph-sm hidden min-w-24 font-bold md:block'}>{accountTranslations.my_rewards.history.status}</div>
          <div className={'paragraph-sm w-full font-bold'}>{accountTranslations.my_rewards.history.expiry_date}</div>
        </div>
        {!rewardsData?.length ? (
          <div>{accountTranslations.my_rewards.history.no_rewards}</div>
        ) : (
          page.map((reward) => {
            const handleExpired = handleExpiryDate(reward.expires_at)
            const valueRemaining = formatMoney((reward.value_remaining ?? 0) * 100)
            return (
              <div className={'flex flex-row items-center gap-4'}>
                <div className={'paragraph-sm w-full flex-grow '}>
                  {reward.definition.name || reward.definition.description}
                </div>
                <div className={'paragraph-sm min-w-20 '}>{valueRemaining}</div>
                <div className={'paragraph-sm hidden min-w-24 md:block perspective-0'}>
                  {<RewardBadge isActive={reward.is_active} remainingDays={handleExpired.remainingDays} />}
                </div>
                <div className={'paragraph-sm w-full '}>
                  {handleExpired.expiryDate}{' '}
                  {handleExpired.remainingDays > 0 && !handleExpired.isExpired && (
                    <>(in {handleExpired.remainingDays} days)</>
                  )}
                  <div className={'block md:hidden mt-1 perspective-0'}>
                    {<RewardBadge isActive={reward.is_active} remainingDays={handleExpired.remainingDays} />}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </AccountSectionWrapper>
  )
}
